import React from 'react'

import { appConfig } from '@/app-config/appConfig'
import { useCompanyFeatureFlags } from '@/app-config/featureFlags'
import { openExternalChatPopup } from '@/lib/genesys-chat.utils'
import { useTranslation } from '@/lib/i18n/i18n'
import { getCurrentLocale } from '@/lib/i18n/locale-utils'
import { useUserPOS } from '@/lib/react-hooks/useUserPOS'
import { eventStopper } from '@/lib/utils'
import BookmarkIcon from '@/refactor/assets/icons/bookmark.svg'
import EnvelopOpenIcon from '@/refactor/assets/icons/envelope-open.svg'
import MessagesIcon from '@/refactor/assets/icons/messages.svg'
import SupportIcon from '@/refactor/assets/icons/support.svg'
import WhatsAppIcon from '@/refactor/assets/icons/whatsapp.svg'
import { DefaultProps } from '@/travelsuit'
import { Locales } from '@/types/locale'

import {
	HelpText,
	ItemContent,
	ItemIcon,
	ItemRow,
	ItemTitle,
	SupportSummaryContainer,
} from './SupportSummary.components'

const getLinkToKnowledgeBase = (lang: Locales) =>
	`https://help.getgoing.com/${lang.slice(0, 2).toLowerCase()}/support/home`

interface IProps extends DefaultProps {
	mobile?: boolean
	onClickOutside?(e: React.MouseEvent<Element>): void
	onClickItem?(e: React.MouseEvent<Element>): void
	closeSideBar?(): void
}

export const SupportSummaryContent = React.forwardRef<HTMLDivElement, IProps>((props, ref) => {
	const { className, mobile, onClickOutside, onClickItem, closeSideBar } = props
	const { t } = useTranslation()

	const companyFeatureFlags = useCompanyFeatureFlags()
	const chatEnabled = appConfig.FEATURE_GENESYS_CLOUD_CHAT || !!companyFeatureFlags?.enable_genesys_chat_bot

	const userPOS = useUserPOS({})
	const cleanWhatsAppRegExp = new RegExp(/\D/g)

	function openLiveChat(e: React.MouseEvent<Element>) {
		openExternalChatPopup(() => {
			onClickOutside?.(e)
			onClickItem?.(e)
			closeSideBar?.()
		})
	}

	const linkToKnowledgeBase = React.useMemo(() => getLinkToKnowledgeBase(getCurrentLocale()), [])

	return (
		<SupportSummaryContainer
			ref={ref}
			className={className}
			mobile={mobile}
			data-test={'SupportSummary'}
			onClick={eventStopper.stopPropagation()}
		>
			<HelpText>{t('support-summary.knowledge-base-section')}</HelpText>
			<ItemRow clickable>
				<ItemIcon src={BookmarkIcon} />
				<ItemContent>
					<ItemTitle
						as="a"
						href={linkToKnowledgeBase}
						data-test="Support-knowledge-base"
						target="_blank"
						rel="noopener noreferrer"
					>
						{t('GetGoing questions? Click here for answers and tips')}
					</ItemTitle>
				</ItemContent>
			</ItemRow>
			<HelpText>{t('support-summary.help-text')}</HelpText>
			{chatEnabled && (
				<ItemRow clickable onClick={mobile ? openLiveChat : undefined} hideBorder>
					<ItemIcon src={MessagesIcon} />
					<ItemContent>
						<ItemTitle onClick={openLiveChat} data-test="Support-LiveChat">
							{t('support-summary.live-chat-title')}
						</ItemTitle>
					</ItemContent>
				</ItemRow>
			)}
			{userPOS?.whatsapp ? (
				<ItemRow clickable hideBorder>
					<ItemIcon src={WhatsAppIcon} />
					<ItemContent>
						<ItemTitle
							as="a"
							href={`https://wa.me/${userPOS?.whatsapp.replace(cleanWhatsAppRegExp, '')}`}
							data-test="Support-WhatsApp"
						>
							{userPOS?.whatsapp}
						</ItemTitle>
					</ItemContent>
				</ItemRow>
			) : null}
			<ItemRow clickable>
				<ItemIcon src={EnvelopOpenIcon} />
				<ItemContent>
					<ItemTitle as="a" href={`mailto:${userPOS?.email}`} data-test="Support-Email">
						{userPOS?.email || ''}
					</ItemTitle>
				</ItemContent>
			</ItemRow>
			<HelpText>{t("We're available 24/7 by phone")}</HelpText>
			<ItemRow clickable>
				<ItemIcon src={SupportIcon} />
				<ItemContent>
					<ItemTitle as="a" href={`tel:${userPOS?.contact_phone || ''}`} data-test="Support-Phone">
						{userPOS?.contact_phone || ''}
					</ItemTitle>
				</ItemContent>
			</ItemRow>
		</SupportSummaryContainer>
	)
})
